// The sign-in hand-off's address work: putting a code on a link, taking it off on arrival, and telling
// which links lead to another Arcos address (api/_handoff.js has the model and the safety rules).
//
// The code rides in the URL FRAGMENT (`#arcos_handoff=…`): a fragment is never sent to a server and
// never appears in a Referer header, so the code is in no access log on the way. It is taken off the
// address before anything else reads it, and it is spent on first use anyway.
//
// Pure apart from `takeHandoffCode`, so it tests under node --test; small, so the website bundle can
// load it without pulling in the Supabase client.

import { HOST_APPS } from '../features/program/hostApps.js';

export const HANDOFF_PARAM = 'arcos_handoff';

const ARCOS_HOSTS = new Set(['www.arcos.network', 'arcos.network']);

/** Is this a host Arcos serves, as the browser can tell without asking? Websites mark their links instead. */
export function isArcosHost(hostname) {
  const h = String(hostname || '').toLowerCase();
  return ARCOS_HOSTS.has(h) || Object.prototype.hasOwnProperty.call(HOST_APPS, h);
}

/** The code in a fragment, and the fragment as it was without it. */
export function handoffCodeIn(hash) {
  const raw = String(hash || '').replace(/^#/, '');
  if (!raw) return { code: null, rest: '' };
  const parts = raw.split('&');
  let code = null;
  const rest = [];
  for (const p of parts) {
    const m = p.match(new RegExp(`^${HANDOFF_PARAM}=([A-Za-z0-9_-]+)$`));
    if (m && !code) code = m[1];
    else if (p) rest.push(p);
  }
  return { code, rest: rest.join('&') };
}

/** The same address carrying a code, any fragment it already had kept in front of it. */
export function withHandoffCode(href, code) {
  const u = new URL(href);
  const { rest } = handoffCodeIn(u.hash);
  u.hash = rest ? `${rest}&${HANDOFF_PARAM}=${code}` : `${HANDOFF_PARAM}=${code}`;
  return u.toString();
}

/**
 * Take a code off this page's address, if one came: the address bar loses it at once, so a reload,
 * a bookmark or a shared link never carries it.
 */
export function takeHandoffCode(win = typeof window !== 'undefined' ? window : null) {
  if (!win?.location?.hash) return null;
  const { code, rest } = handoffCodeIn(win.location.hash);
  if (!code) return null;
  try {
    const { pathname, search } = win.location;
    win.history.replaceState(win.history.state, '', `${pathname}${search}${rest ? `#${rest}` : ''}`);
  } catch { /* a sandboxed frame may refuse; the code is spent on first use regardless */ }
  return code;
}

/**
 * Should a click on this link carry the sign-in across? Only a plain left click, in this tab, on a
 * link to ANOTHER Arcos address: a new tab opens without one, because the code is fetched after the
 * click and a browser blocks a tab opened that late.
 *
 * @returns {string|null} the address to hand off to, or null to let the click do what it does
 */
export function handoffLinkTarget(event, currentHref) {
  if (!event || event.defaultPrevented || event.button !== 0) return null;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
  const a = event.target?.closest?.('a[href]');
  if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return null;
  let url;
  try { url = new URL(a.getAttribute('href'), currentHref); } catch { return null; }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (url.origin === new URL(currentHref).origin) return null;
  if (!a.hasAttribute('data-arcos-handoff') && !isArcosHost(url.hostname)) return null;
  return url.toString();
}
